// Nav badges — the only in-app signal that something is waiting for a person.
//
// The classic app had three of these and the first cut of the rebuild had
// none, which is a quiet regression rather than a visible one: nothing looks
// broken, a submitted timesheet just sits there until somebody thinks to go
// and look. They are back, on the ONE nav, so a badge appears in the sidebar
// at a desk and on the tab bar on a phone from the same source.
//
//   GET /timesheets?status=submitted   → array; its LENGTH is the count
//   GET /dispatch/approve              → { counts: { to_approve } }
//   GET /jobs/blocked-count            → { count }
//   GET /notifications/unread-count    → { count }
//
// Two sources for the Approve badge, because the first one answers a
// different question depending on who asks — see the note above canApprove().
//
// `typeof count === 'number'` and `Array.isArray`, never `data.count || 0`:
// a 200 carrying `{ error: … }` and a genuine zero are different answers, and
// treating them alike is how the classic bell learned to erase a good count.
// That bug is pinned in App.test.js history and is not being reintroduced.

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { get } from '../shared/http';
import { useRole } from '../../hooks/usePermission';
import { useAuth } from '../../App';
import { destinationsFor } from '../shared/navigation';

// ── Who gets an Approve badge, and what it can be counted from ──────────────
//
// This was `const APPROVERS = ['admin', 'manager', 'accounting']` — a fourth
// copy of a rule three other places already state, and it disagreed with all
// three. Both halves of that were wrong, in opposite directions:
//
//   · **accounting was in it and cannot approve anything.**
//     server/database.js:1051-1055 seeds accounting `timesheets: ['read',
//     'read_all']` — `read_all` is a MARKER, not approve — so
//     server/routes/dispatch.js:39-44 `canDispatch` refuses them and
//     shared/navigation.js gives them no Approvals row. Worse than a badge
//     for a screen they do not have: the count itself was a different
//     QUESTION'S answer. server/routes/timesheets.js:318-320 scopes any
//     caller who is not `admin` or `manager` to `ts.user_id = ?`, so
//     GET /timesheets?status=submitted hands them their OWN submitted weeks.
//     Their own unapproved timesheet was being counted as work waiting on
//     them.
//
//   · **a promoted lead was not in it and CAN approve.** A worker granted
//     `timesheets.approve` through extra_permissions passes canDispatch
//     (dispatch.js:41-43) and gets the row — navigation.js's approve
//     destination carries `needs: ['timesheets', 'approve']`. The screen was
//     reachable; the only signal that something was waiting on it was not.
//
// So the badge asks the nav table instead of restating the rule. Whoever the
// menu opens Approvals for is whoever gets its badge, by construction — the
// same destinationsFor() AppShell renders the row from and AppRouter's
// `Allowed` guards the URL with.

/** Can this person reach /approve at all? The ONE nav table is the authority. */
function canApprove(role, permissions) {
  return destinationsFor(role, { permissions }).some((d) => d.key === 'approve');
}

/**
 * ...and which endpoint answers the queue FOR THEM.
 *
 * `GET /timesheets?status=submitted` is every submitted sheet in every week —
 * but only for `admin` and `manager`. timesheets.js:318-320 silently narrows
 * it to the caller's own rows for anybody else, which is why the old list
 * could not simply be widened: giving a lead the same query would have handed
 * them the accounting bug rather than a badge.
 *
 * Everyone else who can approve reads `GET /dispatch/approve` instead —
 * `requireDispatch`, so it admits exactly the people who can open the screen,
 * and `counts.to_approve` (dispatch.js:196, 209) is the number of submitted
 * sheets in the week. It is THIS WEEK rather than every week, which is a
 * narrower answer than admin's and an honest one: the screen it badges is one
 * week too (dispatch.js:187-189). Its roster is `role NOT IN ('admin',
 * 'accounting')`, so an office account's own sheet can never re-enter the
 * count by the back door.
 *
 * Not folded into one source deliberately. Moving admin and manager onto the
 * week count would shrink a badge that today reaches back over older weeks,
 * and a submitted sheet quietly dropping off a payroll queue is the kind of
 * plausible wrong number CLAUDE.md calls the worst outcome available here.
 */
const READS_EVERY_WEEK = ['admin', 'manager'];

const numberOr = (v, value) => (typeof value === 'number' && Number.isFinite(value) ? value : v);

export function useNavBadges() {
  const role = useRole();
  // The merged permission set, not the role label — a per-user grant is what
  // opens the row through `needs`. `user` is null before /me hydrates, and
  // navigation.js reads a missing set as "no extra rows" rather than throwing.
  const { user } = useAuth();
  const approver = canApprove(role, user && user.permissions);
  const everyWeek = approver && READS_EVERY_WEEK.includes(role);
  const qc = useQueryClient();

  const pending = useQuery({
    queryKey: ['badges', 'pending-timesheets'],
    queryFn: () => get('/timesheets?status=submitted'),
    enabled: everyWeek,
    retry: false,
    staleTime: 60_000,
  });

  // The same badge for everybody else who can approve. One fetch on mount and
  // no interval, like the query above — the rate-limiter arithmetic below is
  // about polling, and this does not poll.
  const week = useQuery({
    queryKey: ['badges', 'approve-week'],
    queryFn: () => get('/dispatch/approve'),
    enabled: approver && !everyWeek,
    retry: false,
    staleTime: 60_000,
  });

  const blocked = useQuery({
    queryKey: ['badges', 'blocked-jobs'],
    queryFn: () => get('/jobs/blocked-count'),
    retry: false,
    staleTime: 60_000,
  });

  // The bell is the one that refetches, so it is the one that can erase a good
  // value.
  //
  // 60s, not the classic app's 15s, and the reason is arithmetic rather than
  // taste. server/server.js:222 rate-limits /api/ at 1000 requests per 15
  // minutes PER IP, and a crew shares one — an office router, or a carrier
  // NAT out in the field. At 15s each open tab spends 60 of that budget on
  // this one poll alone; ten phones is 600 before anybody has loaded a screen.
  // A browser run of scripts/ui-check.js hit the limiter and every scenario
  // after it failed with 429, which is the same wall a crew would hit on a
  // busy morning.
  //
  // Almost nothing is lost. react-query does not run the interval while the
  // tab is hidden, `refetchOnWindowFocus` makes it immediate the moment
  // somebody looks, the Notifications screen fires `notifications-changed`
  // when it marks anything read, and `shell/liveUpdates.js` invalidates this
  // key the moment the server pushes a notification over the socket. The only
  // case that gets slower is a badge appearing while a person is staring at an
  // idle screen with the socket down, and a minute is not a wrong answer there.
  //
  // That middle clause was FALSE until 2026-09-09 and is worth naming, because
  // a note that has quietly become false gets followed: the listener below has
  // always existed and nothing in the tree dispatched the event, so marking
  // everything read left this badge on its old number for up to a minute.
  // office/insight/Notifications.js:302 dispatches it now.
  const unread = useQuery({
    queryKey: ['badges', 'unread-notifications'],
    queryFn: () => get('/notifications/unread-count'),
    retry: false,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });

  // The Notifications screen fires this after marking anything read, so the
  // badge follows the screen without either importing the other.
  useEffect(() => {
    const refresh = () => qc.invalidateQueries({ queryKey: ['badges', 'unread-notifications'] });
    window.addEventListener('notifications-changed', refresh);
    return () => window.removeEventListener('notifications-changed', refresh);
  }, [qc]);

  return {
    // `Array.isArray` / `typeof === 'number'` on both, never `|| 0`: a 200
    // carrying `{ error: … }` and a genuine zero are different answers.
    approve: everyWeek
      ? (Array.isArray(pending.data) ? pending.data.length : 0)
      : numberOr(0, week.data && week.data.counts && week.data.counts.to_approve),
    schedule: numberOr(0, blocked.data && blocked.data.count),
    notifications: numberOr(0, unread.data && unread.data.count),
  };
}

export default useNavBadges;
